
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useProfileManagement } from "./useProfileManagement";
import { useAuthNavigation } from "./useAuthNavigation";
import type { AuthChangeEvent } from "@supabase/supabase-js";

export const useAuthRedirect = (
  loginType: string,
  setLoginType: (type: "owner" | "admin") => void
) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createOrUpdateProfile } = useProfileManagement();
  const { handleAuthNavigation } = useAuthNavigation();

  useEffect(() => {
    let isSubscribed = true;

    const checkProfileCompletion = async (userId: string) => {
      const { data: ownerData, error: ownerError } = await supabase
        .from('owner_data')
        .select('business_type_id, company_name')
        .eq('owner_id', userId)
        .single();

      if (ownerError) {
        console.error("Erreur lors de la vérification du profil:", ownerError);
        return false;
      }

      return ownerData && ownerData.business_type_id && ownerData.company_name;
    };

    const handleTokenCheck = async () => {
      if (!isSubscribed) return;

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) throw sessionError;
        
        if (session?.user) {
          console.log("Session user found:", session.user);
          
          const isProfileComplete = await checkProfileCompletion(session.user.id);
          
          if (!isProfileComplete) {
            console.log("Profile incomplete, redirecting to complete registration");
            navigate("/complete-registration", { replace: true });
            return;
          }

          const profile = await createOrUpdateProfile(
            session.user.id,
            loginType as "owner" | "admin"
          );

          console.log("Profile created/updated:", profile);

          if (profile) {
            handleAuthNavigation(profile.login_type, profile.user_type);
          } else {
            handleAuthNavigation(loginType);
          }
        }
      } catch (error) {
        console.error("Error in token check:", error);
        toast.error("Erreur lors de la connexion");
        navigate("/login-owner", { replace: true });
      }
    };

    const handleAuthStateChange = async (
      event: AuthChangeEvent,
      session: any
    ) => {
      if (!isSubscribed) return;

      console.log("Auth state changed in useAuthRedirect:", event, session);

      switch (event) {
        case "SIGNED_IN":
          if (session?.user?.id) {
            try {
              const isProfileComplete = await checkProfileCompletion(session.user.id);
              
              if (!isProfileComplete) {
                console.log("Profile incomplete, redirecting to complete registration");
                navigate("/complete-registration", { replace: true });
                return;
              }

              const profile = await createOrUpdateProfile(
                session.user.id,
                loginType as "owner" | "admin"
              );

              console.log("Profile after sign in:", profile);

              if (profile) {
                handleAuthNavigation(profile.login_type, profile.user_type);
              } else {
                handleAuthNavigation(loginType);
              }
            } catch (error) {
              console.error("Error in auth state change:", error);
              toast.error("Erreur lors de la connexion");
              navigate("/login-owner", { replace: true });
            }
          }
          break;

        case "SIGNED_OUT":
          console.log("User signed out");
          navigate("/login-owner", { replace: true });
          break;

        case "USER_UPDATED":
          toast.success("Mot de passe mis à jour avec succès");
          navigate("/login-owner", { replace: true });
          break;
      }
    };

    // Vérifier immédiatement la session au montage du composant
    handleTokenCheck();

    // Mettre en place l'écouteur des changements d'état d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      handleAuthStateChange
    );

    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
    };
  }, [navigate, loginType]);
};
